import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { ShopHeader } from "../components/site/ShopHeader";
import { useCart } from "../contexts/CartContext";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | timeout
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    const poll = async () => {
      if (attempts.current >= 8) { if (active) setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") { setOrder(data.order); setState("paid"); clear(); return; }
        if (data.status === "expired") { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (active) setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#F6F5F0]">
      <ShopHeader />
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        {state === "polling" && (
          <div data-testid="order-polling">
            <Loader2 className="w-12 h-12 text-[#1F5A34] animate-spin mx-auto" />
            <h1 className="mt-6 font-display font-bold text-3xl text-[#1A1C19]">Confirming your payment…</h1>
            <p className="mt-2 text-[#4A4D48]">Please wait a moment.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="order-success">
            <CheckCircle2 className="w-16 h-16 text-[#1F5A34] mx-auto" />
            <h1 className="mt-6 font-display font-bold text-4xl text-[#1A1C19]">Thank you for your order!</h1>
            <p className="mt-3 text-[#4A4D48]">Your payment was successful{order ? ` — total $${order.total.toFixed(2)}` : ""}.</p>
            <div className="mt-8 flex gap-3 justify-center">
              <Link to="/account" data-testid="view-orders" className="px-6 py-3 rounded-full bg-[#1F5A34] text-[#F6F5F0] font-semibold">View my orders</Link>
              <Link to="/shop" className="px-6 py-3 rounded-full border border-[#1F5A34] text-[#1F5A34] font-semibold">Continue shopping</Link>
            </div>
          </div>
        )}
        {(state === "failed" || state === "timeout") && (
          <div data-testid="order-failed">
            <XCircle className="w-16 h-16 text-red-600 mx-auto" />
            <h1 className="mt-6 font-display font-bold text-3xl text-[#1A1C19]">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="mt-3 text-[#4A4D48]">{state === "timeout" ? "Your payment is taking longer than expected. Check your orders shortly." : "We couldn't confirm your payment."}</p>
            <Link to="/shop" className="mt-8 inline-block px-6 py-3 rounded-full bg-[#1F5A34] text-[#F6F5F0] font-semibold">Back to shop</Link>
          </div>
        )}
      </div>
    </div>
  );
}
